import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Stack,
  Chip,
  Avatar,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Message as MessageIcon,
  AttachFile as AttachIcon,
  Person as PersonIcon,
  Pets as PetIcon,
  CalendarToday as CalendarIcon,
  Star as StarIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { 
  shelterService, 
  getAdoptionApplications, 
  getAdoptionApplication,
  scheduleVisit,
  approveApplication,
  rejectApplication,
  AdoptionApplication,
  ApplicationTimeline
} from '@services/sheltersService';


const ShelterApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applications, setApplications] = useState<AdoptionApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState<AdoptionApplication | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [actionNote, setActionNote] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleNotes, setScheduleNotes] = useState('');

  const tabs = [
    { label: 'În Așteptare', value: 'pending', count: 0 },
    { label: 'Programări', value: 'scheduled', count: 0 },
    { label: 'Aprobate', value: 'approved', count: 0 },
    { label: 'Respinse', value: 'rejected', count: 0 }
  ];

  useEffect(() => {
    loadApplications();
  }, [activeTab]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      
      if (!user?.shelterId) {
        throw new Error('Shelter ID not found');
      }

      const status = tabs[activeTab].value;
      const response = await getAdoptionApplications({ 
        status: status === 'all' ? undefined : status,
        page: 1,
        limit: 50
      });
      
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Error loading applications:', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleViewApplication = (application: AdoptionApplication) => {
    setSelectedApplication(application);
    setDetailDialogOpen(true);
  };

  const handleScheduleVisit = (application: AdoptionApplication) => {
    setSelectedApplication(application);
    setScheduleDialogOpen(true);
  };

  const handleApproveReject = (application: AdoptionApplication, action: 'approve' | 'reject') => {
    setSelectedApplication(application);
    setActionType(action);
    setActionDialogOpen(true);
  };

  const handleSubmitAction = async () => {
    try {
      if (!selectedApplication) return;

      if (actionType === 'approve') {
        await approveApplication(selectedApplication.id, { notes: actionNote });
      } else {
        await rejectApplication(selectedApplication.id, { notes: actionNote });
      }
      
      setActionDialogOpen(false);
      setActionNote('');
      loadApplications();
    } catch (error) {
      console.error('Error processing application:', error);
    }
  };

  const handleSubmitSchedule = async () => {
    try {
      if (!selectedApplication || !scheduleDate) return;

      await scheduleVisit(selectedApplication.id, {
        scheduledDate: scheduleDate,
        notes: scheduleNotes
      });
      
      setScheduleDialogOpen(false);
      setScheduleDate('');
      setScheduleNotes('');
      loadApplications();
    } catch (error) {
      console.error('Error scheduling visit:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'scheduled': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <ScheduleIcon />;
      case 'scheduled': return <CalendarIcon />;
      case 'approved': return <ApproveIcon />;
      case 'rejected': return <RejectIcon />;
      default: return <ScheduleIcon />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Cereri de Adopție
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestionează cererile de adopție și programările
        </Typography>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.value}
              label={
                <Badge badgeContent={tab.count} color="primary">
                  {tab.label}
                </Badge>
              }
            />
          ))}
        </Tabs>
      </Card>

      {/* Applications List */}
      {applications.length === 0 ? (
        <Alert severity="info">
          Nu există cereri în această categorie
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Aplicant</TableCell>
                <TableCell>Animal</TableCell>
                <TableCell>Scor Pre-screening</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((application) => (
                <TableRow key={application.id}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {application.applicant.firstName} {application.applicant.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {application.applicant.email}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar>
                        <PetIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {application.animal.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {application.animal.breed}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${application.preScreeningScore}/100`}
                      color={getScoreColor(application.preScreeningScore)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(application.status)}
                      label={application.status}
                      color={getStatusColor(application.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(application.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Vezi detalii">
                        <IconButton
                          size="small"
                          onClick={() => handleViewApplication(application)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {application.status === 'pending' && (
                        <>
                          <Tooltip title="Programează vizită">
                            <IconButton
                              size="small"
                              onClick={() => handleScheduleVisit(application)}
                            >
                              <ScheduleIcon />
                            </IconButton>
                          </Tooltip>
                          {user?.role === 'shelter_admin' && (
                            <>
                              <Tooltip title="Aprobă">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleApproveReject(application, 'approve')}
                                >
                                  <ApproveIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Respinge">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleApproveReject(application, 'reject')}
                                >
                                  <RejectIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Application Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalii Cerere de Adopție</DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              {/* Applicant Info */}
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Informații Aplicant
                  </Typography>
                  <Stack spacing={1}>
                    <Typography><strong>Nume:</strong> {selectedApplication.applicant.firstName} {selectedApplication.applicant.lastName}</Typography>
                    <Typography><strong>Email:</strong> {selectedApplication.applicant.email}</Typography>
                    <Typography><strong>Telefon:</strong> {selectedApplication.applicant.phone}</Typography>
                    <Typography><strong>Adresă:</strong> {selectedApplication.applicant.address}, {selectedApplication.applicant.city}</Typography>
                  </Stack>
                </CardContent>
              </Card>

              {/* Animal Info */}
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Animal Dorit
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar>
                      <PetIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{selectedApplication.animal.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedApplication.animal.breed} • {selectedApplication.animal.species}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Notes and Attachments */}
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Note și Atașamente
                  </Typography>
                  <Typography variant="body1" mb={2}>
                    {selectedApplication.notes}
                  </Typography>
                  {selectedApplication.attachments.length > 0 && (
                    <Stack direction="row" spacing={1}>
                      {selectedApplication.attachments.map((attachment, index) => (
                        <Chip
                          key={index}
                          icon={<AttachIcon />}
                          label={attachment}
                          size="small"
                        />
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" mb={2}>
                    Istoric
                  </Typography>
                  <Stack spacing={1}>
                    {selectedApplication.timeline.map((event) => (
                      <Box key={event.id} display="flex" alignItems="center" gap={2}>
                        <Chip label={event.status} size="small" />
                        <Typography variant="body2">{event.note}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(event.createdAt).toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Închide</Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Visit Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Programează Vizită</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="datetime-local"
              label="Data și ora vizitei"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Note pentru vizită"
              value={scheduleNotes}
              onChange={(e) => setScheduleNotes(e.target.value)}
              placeholder="Instrucțiuni speciale sau detalii..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Anulează</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitSchedule}
            disabled={!scheduleDate}
          >
            Programează
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve/Reject Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? 'Aprobă Cererea' : 'Respinge Cererea'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Note"
            value={actionNote}
            onChange={(e) => setActionNote(e.target.value)}
            placeholder={`Motivul pentru ${actionType === 'approve' ? 'aprobare' : 'respingere'}...`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Anulează</Button>
          <Button 
            variant="contained" 
            color={actionType === 'approve' ? 'success' : 'error'}
            onClick={handleSubmitAction}
          >
            {actionType === 'approve' ? 'Aprobă' : 'Respinge'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ShelterApplicationsPage;
